import { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { CommentItem } from './CommentItem';
import { CCommentData } from '../hooks/dto/taskItem.dto';
import { colors } from '@src/styles/colors';
import { useTasksContext } from '../context/TasksContext';

interface CommentsSectionProps {
  comments: CCommentData[];
}

export const CommentsSection = ({ comments }: CommentsSectionProps) => {
  const { handleCreateComment } = useTasksContext();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (newComment.trim() === '') return;
    setIsSubmitting(true);
    try {
      await handleCreateComment(newComment);
      setNewComment('');
    } catch (error) {
      console.error('Error creating comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '16px' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
        {comments && comments.length > 0 ? (
          [...comments]
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
            .map((comment) => <CommentItem key={comment.id} comment={comment} />)
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '32px',
              color: colors.primary.gray,
            }}
          >
            <Typography variant="body2">Nenhum comentário ainda. Seja o primeiro a comentar!</Typography>
          </Box>
        )}
      </Box>
      <Box>
        <TextField
          fullWidth
          multiline
          rows={1}
          placeholder="Adicione um comentário..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={handleKeyPress}
          disabled={isSubmitting}
          InputProps={{
            endAdornment: (
              <Button
                variant="contained"
                size="small"
                startIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={handleSubmit}
                disabled={isSubmitting || newComment.trim() === ''}
                sx={{
                  minWidth: 'auto',
                  padding: '4px 8px',
                  backgroundColor: colors.primary,
                  '&:hover': { backgroundColor: colors.primary.yellow },
                }}
              >
                {!isSubmitting}
              </Button>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: colors.background.secondary,
              color: colors.primary.white,
              border: `1px solid ${colors.background.secondary}`,
            },
            '& .MuiInputBase-input::placeholder': {
              color: colors.primary.gray,
              opacity: 0.7,
            },
          }}
        />
      </Box>
    </Box>
  );
};
